import React from "react";
import { View, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { PreviewIcon } from "@/components/screens/shared/PreviewIcon";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import { ShiftType, ShiftLocationType } from "@/types/db/shifts";
import { LocationType } from "@/types";
import { StatusConfig } from "./types";
import { StatusIcon } from "./StatusIcon";
import { LocationDisplay } from "./LocationDisplay";
import { Visible } from "@/components/Table/types";

type ShiftHeaderProps = {
  shift: ShiftType;
  compact?: boolean;
  config?: StatusConfig | null;
  location?: LocationType | ShiftLocationType;
  visibleFields?: Visible<string>[];
};

export function ShiftHeader({ shift, compact = false, config, location, visibleFields }: ShiftHeaderProps) {
  const { colors } = useTheme();
  const { formatTime, formatDuration } = useDateTime();

  // Helper to check if a field is visible
  const isVisible = (fieldName: string): boolean => {
    if (!visibleFields?.length) return true; // Show all by default
    const entry = visibleFields.find((v) => v.name === fieldName);
    return entry ? entry.visible !== false : true;
  };

  const startTime = shift.start_time ? formatTime(shift.start_time) : "--";
  const endTime = shift.end_time ? formatTime(shift.end_time) : "--";
  const duration = shift.start_time && shift.end_time ? formatDuration(shift.start_time, shift.end_time) : "—";
  const durationHours = duration.match(/[\d.]+/)?.[0] || "0";

  // Get status color
  const getStatusColor = () => {
    switch (shift.status) {
      case "APPROVED":
      case "ADVANCED":
        return colors.complete.border;
      case "CANCELED":
        return colors.attention.border;
      case "CANCELED - BILLABLE":
        return colors.neutral.border;
      case "COMPLETE":
        return colors.todo.border;
      default:
        return colors.pending.border;
    }
  };

  return (
    <View style={[styles.header, compact && styles.headerCompact]}>
      <View style={styles.headerLeft}>
        {/* Name row with status inline */}
        <View style={styles.nameRow}>
          {isVisible("name") && (
            <ThemedText style={[styles.projectName, compact && styles.textCompact]} numberOfLines={1}>
              {shift?.name || "Untitled Shift"}
            </ThemedText>
          )}
          {isVisible("status") && shift.status && (
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor() }]}>
              <ThemedText style={styles.statusText}>{shift.status}</ThemedText>
            </View>
          )}
        </View>
        {isVisible("client") && shift.project?.client?.name && (
          <View style={styles.projectRow}>
            <PreviewIcon entityType="client" size={12} />
            <ThemedText numberOfLines={1} style={styles.timeText}>
              {shift.project.client.name}
            </ThemedText>
          </View>
        )}
        {isVisible("project") && (
          <View style={styles.projectRow}>
            <PreviewIcon entityType="project" size={12} />
            <ThemedText numberOfLines={1} style={styles.timeText}>
              {shift.project?.name || "Untitled Project"}
            </ThemedText>
          </View>
        )}
        <View style={styles.timeRow}>
          <MaterialCommunityIcons name="clock-outline" size={compact ? 10 : 12} color={colors.placeholder} />
          <ThemedText style={[styles.timeText, compact && styles.textCompact]}>
            {startTime} - {durationHours}h
          </ThemedText>
        </View>
        {isVisible("location") && <LocationDisplay location={location} compact={compact} />}
      </View>

      {config && <StatusIcon config={config} compact={compact} />}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 8,
  },
  headerCompact: {
    padding: 6,
  },
  headerLeft: {
    flex: 1,
    gap: 2,
  },
  nameRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  projectRow: {
    flex: 1,
    flexDirection: "row",
    gap: 4,
  },
  projectName: {
    fontSize: 13,
    fontWeight: "600",
    flex: 1,
  },
  textCompact: {
    fontSize: 11,
  },
  timeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  timeText: {
    fontSize: 11,
    opacity: 0.8,
  },
  duration: {
    fontSize: 10,
    opacity: 0.6,
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 9,
    fontWeight: "600",
    textTransform: "uppercase",
  },
});
